import {
  AdjustDrawerDialog,
  CloseDrawerDialog,
  StartDrawerDialog,
  WorkstationPairingDialog,
} from '@/components/pos/CashDrawerDialogs'
import {
  useCashDrawerStatus,
  useCashDrawerWorkstations,
  useCurrentCashDrawer,
} from '@/lib/services/cash-drawers/cash-drawers.query'
import { useCashDrawerDevicePairing } from '@/lib/services/cash-drawers/cash-drawer-device'
import type { CashDrawerSession } from '@/lib/services/cash-drawers/cash-drawers.types'
import type { OrganizationIdInput } from '@/lib/organization-scoping'
import { useState } from 'react'
import { toast } from 'sonner'

export type CashDrawerDialogAction =
  | { type: 'start'; workstationId: number }
  | { type: 'adjust'; session: CashDrawerSession }
  | { type: 'close'; session: CashDrawerSession }
  | { type: 'pair' }

export function useCashDrawerControls(organizationId: OrganizationIdInput) {
  const cashDrawerStatusQuery = useCashDrawerStatus()
  // A failed refetch keeps the last status it loaded; only a status that never loaded hides the
  // controls, so a network blip while rechecking doesn't make the drawer disappear.
  const trackingEnabled = cashDrawerStatusQuery.data?.enabled === true
  const workstationsQuery = useCashDrawerWorkstations({ enabled: trackingEnabled })
  const { data: allWorkstations = [] } = workstationsQuery
  const workstations = allWorkstations.filter(
    (workstation) => workstation.is_active && workstation.location?.is_active
  )
  const { workstationId, pairWorkstation } = useCashDrawerDevicePairing(organizationId)
  // Standing till controls require a saved pairing; admission's sole-register fallback does not save one.
  const pairedWorkstation = workstations.find(
    (workstation) => Number(workstation.id) === Number(workstationId)
  )
  const currentDrawerQuery = useCurrentCashDrawer(pairedWorkstation?.id, {
    enabled: trackingEnabled && Boolean(pairedWorkstation),
  })
  const currentSession = currentDrawerQuery.data?.session ?? null
  const [dialogAction, setDialogAction] = useState<CashDrawerDialogAction | null>(null)

  // The open drawer belongs to its register, so a device can switch registers at any time.
  const handlePairWorkstation = (nextWorkstationId: number, successMessage: string) => {
    pairWorkstation(nextWorkstationId)
    toast.success(successMessage)
    setDialogAction(null)
  }

  return {
    cashDrawerStatusQuery,
    currentDrawerQuery,
    currentSession,
    dialogAction,
    initialLoadFailed: trackingEnabled && workstationsQuery.isError && !workstationsQuery.data,
    openDialog: (action: CashDrawerDialogAction) => setDialogAction(action),
    pairedWorkstation,
    setDialogAction,
    trackingEnabled,
    workstationId,
    workstations,
    workstationsQuery,
    handlePairWorkstation,
  }
}

export type CashDrawerControls = ReturnType<typeof useCashDrawerControls>

export function CashDrawerDialogHost({
  controls,
  pairSuccessMessage,
}: {
  controls: CashDrawerControls
  pairSuccessMessage: string
}) {
  const {
    currentSession,
    dialogAction,
    handlePairWorkstation,
    pairedWorkstation,
    setDialogAction,
    workstationId,
    workstations,
  } = controls
  const handleDialogOpenChange = (open: boolean) => {
    if (!open) setDialogAction(null)
  }

  return (
    <>
      {dialogAction?.type === 'start' ? (
        <StartDrawerDialog
          open
          onOpenChange={handleDialogOpenChange}
          workstationId={dialogAction.workstationId}
        />
      ) : null}
      {dialogAction?.type === 'adjust' ? (
        <AdjustDrawerDialog
          key={`adjust-${dialogAction.session.id}`}
          open
          onOpenChange={handleDialogOpenChange}
          session={dialogAction.session}
        />
      ) : null}
      {dialogAction?.type === 'close' && currentSession ? (
        <CloseDrawerDialog
          key={`close-${currentSession.id}`}
          open
          onOpenChange={handleDialogOpenChange}
          session={currentSession}
        />
      ) : null}
      {dialogAction?.type === 'pair' ? (
        <WorkstationPairingDialog
          open
          onOpenChange={handleDialogOpenChange}
          workstations={workstations}
          workstationId={pairedWorkstation?.id ?? workstationId}
          onPair={(nextWorkstationId: number) =>
            handlePairWorkstation(nextWorkstationId, pairSuccessMessage)
          }
        />
      ) : null}
    </>
  )
}
